import { shopifyComponentCopy } from '@testfeed/product-language'
import { CardGrid, CardGridItem } from '../../CardGrid'
import { useAnimateWhenVisible } from '../../../hooks/useAnimateWhenVisible'
import { InfoButton, InfoPopover } from '../../shared/PageHelp'
import { HomeArt, type ArtName } from '../art/HomeArt'
import '../../results/RecommendationCardItem.css'

const INFO_ID = 'app-home-info-capabilities'

export type CapKind = 'products' | 'store' | 'product-insights' | 'insights' | 'opportunities'

const CAP_ART: Record<CapKind, ArtName> = {
  products: 'products',
  store: 'store',
  'product-insights': 'productInsights',
  insights: 'insights',
  opportunities: 'opportunities',
}

export interface Capability {
  kind: CapKind
  group: 'Test' | 'Learn' | 'Improve'
  title: string
  body: string
  /** Where the card goes when it's open. */
  open: () => void
  /** Why it can't open yet, or null when it can. */
  lockedReason: string | null
}

interface Props {
  items: Capability[]
  onLocked: (c: Capability) => void
}

function Card({ c, onLocked }: { c: Capability; onLocked: Props['onLocked'] }) {
  const { ref, hasBeenVisible } = useAnimateWhenVisible<HTMLDivElement>()
  return (
    // The fade-in lives on this wrapping div, not s-box itself: the typed Shopify web-component
    // bindings only expose key/ref/slot/children, no className or style. The grid stretches this
    // div to the row's full height (CardGridItem's default grid stretch), so it needs its own
    // height: 100% for s-box's blockSize="100%" below to have something to be 100% of.
    <div
      ref={ref}
      className={`tf-card-enter${hasBeenVisible ? ' tf-card-enter--visible' : ''}`}
      style={{ height: '100%' }}
    >
      <s-box border="base" borderRadius="base" overflow="hidden" background="base" blockSize="100%">
        <s-clickable
          onClick={() => (c.lockedReason ? onLocked(c) : c.open())}
          accessibilityLabel={c.lockedReason ? `${c.title}. ${c.lockedReason}` : `Open ${c.title}`}
          blockSize="100%"
        >
          <HomeArt name={CAP_ART[c.kind]} alt={c.title} maxWidth={420} background="transparent" />
          <s-divider></s-divider>
          <s-box padding="small">
            <s-grid gap="small-400">
              <s-text color="subdued">{c.group}</s-text>
              <s-grid gridTemplateColumns="1fr auto" alignItems="center" gap="small-200">
                <s-heading>{c.title}</s-heading>
                <s-icon type={c.lockedReason ? 'lock' : 'arrow-right'} tone="neutral"></s-icon>
              </s-grid>
              <s-paragraph color="subdued">{c.body}</s-paragraph>
            </s-grid>
          </s-box>
        </s-clickable>
      </s-box>
    </div>
  )
}

function CompactCard({ c, onLocked }: { c: Capability; onLocked: Props['onLocked'] }) {
  const { ref, hasBeenVisible } = useAnimateWhenVisible<HTMLDivElement>()
  return (
    <div
      ref={ref}
      className={`tf-card-enter${hasBeenVisible ? ' tf-card-enter--visible' : ''}`}
      style={{ height: '100%' }}
    >
      <s-box border="base" borderRadius="base" overflow="hidden" background="base" blockSize="100%">
        <s-clickable
          onClick={() => (c.lockedReason ? onLocked(c) : c.open())}
          accessibilityLabel={c.lockedReason ? `${c.title}. ${c.lockedReason}` : `Open ${c.title}`}
          blockSize="100%"
        >
          <s-box padding="base">
            <s-grid gap="small-400">
              <HomeArt name={CAP_ART[c.kind]} alt={c.title} size={160} center background="transparent" />
              <s-heading>{c.title}</s-heading>
              <s-paragraph color="subdued">{c.body}</s-paragraph>
            </s-grid>
          </s-box>
        </s-clickable>
      </s-box>
    </div>
  )
}

/** Media card composition: things TestFeed does, drawn instead of described. The two Test cards
 * run full-width illustrations; the Learn/Improve cards run compact alongside them in one row. */
export function Capabilities({ items, onLocked }: Props) {
  const copy = shopifyComponentCopy.appHome
  const tests = items.filter((c) => c.group === 'Test')
  const rest = items.filter((c) => c.group !== 'Test')
  const setupLocked = items.some((c) => c.lockedReason === copy.capabilitiesOpensAfterSetup)
  return (
    <>
      <s-section accessibilityLabel={copy.capabilitiesHeading}>
        <s-grid gap="base">
          <s-grid gridTemplateColumns="1fr auto auto" alignItems="center" gap="small">
            <s-heading>{copy.capabilitiesHeading}</s-heading>
            {setupLocked && <s-text color="subdued">{copy.capabilitiesOpensAfterSetup}</s-text>}
            <InfoButton id={INFO_ID} heading={copy.capabilitiesHeading} />
          </s-grid>
          <CardGrid maxColumns={2}>
            {tests.map((c) => (
              <CardGridItem key={c.kind}>
                <Card c={c} onLocked={onLocked} />
              </CardGridItem>
            ))}
          </CardGrid>
          <CardGrid maxColumns={3} minColumnWidth={200}>
            {rest.map((c) => (
              <CardGridItem key={c.kind}>
                <CompactCard c={c} onLocked={onLocked} />
              </CardGridItem>
            ))}
          </CardGrid>
        </s-grid>
      </s-section>
      <InfoPopover
        id={INFO_ID}
        why="These cards are shortcuts to the main things TestFeed does. Locked ones open once your store's connected, a plan's chosen, and your first sync is done."
        how="Availability is worked out from your connection, plan, and sync status here on Home, not from a separate setting."
      />
    </>
  )
}
